// Shared time-range + bucket-interval controls for the Cost section. Mirrors
// the range/interval pattern used by the apigen Statistics page so the Cost
// pages feel consistent with the rest of the SPA ("breakable by time"), plus
// two windows the rest of the SPA has no equivalent for: `lastCycle` (the last
// complete calendar month, i.e. a billing cycle) and `custom` (explicit dates).

import {
  endOfDay,
  endOfMonth,
  parse as parseDate,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';

export type RangeKey = '24h' | '7d' | '30d' | 'lastCycle' | 'custom';
export type IntervalKey = '5m' | '15m' | '1h' | '6h' | '1d';

export const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': '7 days',
  '30d': '30 days',
  lastCycle: 'Last cycle',
  custom: 'Custom',
};

/** Window length for the rolling presets. Absent for `lastCycle`/`custom`. */
export const RANGE_MS: Record<'24h' | '7d' | '30d', number> = {
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
  '30d': 30 * 24 * 60 * 60_000,
};

export const INTERVAL_LABELS: Record<IntervalKey, string> = {
  '5m': '5 min',
  '15m': '15 min',
  '1h': '1 hour',
  '6h': '6 hours',
  '1d': '1 day',
};

export const INTERVAL_MS: Record<IntervalKey, number> = {
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '1d': 24 * 60 * 60_000,
};

/** The `yyyy-MM-dd` pair behind the `custom` preset (as `<input type="date">` gives it). */
export interface CustomRange {
  from: string;
  to: string;
}

export const EMPTY_CUSTOM_RANGE: CustomRange = { from: '', to: '' };

export interface ResolvedRange {
  from: Date;
  to: Date;
}

function parseDayInput(value: string): Date | null {
  if (!value) return null;
  const parsed = parseDate(value, 'yyyy-MM-dd', new Date());
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Turn a preset (plus the custom dates, when that preset is selected) into an
 * absolute window. Returns `null` only for a `custom` selection that isn't a
 * usable range yet — a missing endpoint, or `from` after `to` — which the
 * pages surface as a prompt rather than fetching a nonsense window.
 *
 * `custom` covers whole days in local time so picking a single day yields that
 * day in full, and `lastCycle` covers the previous calendar month end to end.
 */
export function resolveRange(
  key: RangeKey,
  custom: CustomRange = EMPTY_CUSTOM_RANGE,
  now: Date = new Date(),
): ResolvedRange | null {
  if (key === 'lastCycle') {
    const lastMonth = subMonths(now, 1);
    return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
  }
  if (key === 'custom') {
    const from = parseDayInput(custom.from);
    const to = parseDayInput(custom.to);
    if (!from || !to) return null;
    const window = { from: startOfDay(from), to: endOfDay(to) };
    return window.from > window.to ? null : window;
  }
  return { from: new Date(now.getTime() - RANGE_MS[key]), to: now };
}

/** Human label for a resolved window, shown next to the preset buttons. */
export function describeRange(range: ResolvedRange): string {
  const sameYear = range.from.getFullYear() === range.to.getFullYear();
  const fmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${fmt.format(range.from)} – ${fmt.format(range.to)}`;
}

/**
 * Bucket width that keeps a window readable: roughly 30-300 bars, capped at a
 * day. Used as the default whenever the window changes, including for `custom`
 * spans the fixed presets say nothing about.
 */
export function intervalForSpan(spanMs: number): IntervalKey {
  if (spanMs <= 26 * 60 * 60_000) return '5m';
  if (spanMs <= 3 * 24 * 60 * 60_000) return '1h';
  if (spanMs <= 8 * 24 * 60 * 60_000) return '6h';
  return '1d';
}

/** `date-fns` pattern for a bucket label, given the bucket width and window span. */
export function bucketLabelFormat(stepMs: number, spanMs: number): string {
  if (stepMs >= INTERVAL_MS['1d']) return 'MMM d';
  return spanMs <= 26 * 60 * 60_000 ? 'HH:mm' : 'M/d HH:mm';
}

/** Format a USD amount for display, scaling precision to the magnitude. */
export function formatUsd(value: number): string {
  if (!value || value <= 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(5)}`;
  if (value < 1) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

/** Percentage of a total, for share columns. Guards a zero total. */
export function formatShare(value: number, total: number): string {
  if (!total) return '—';
  return `${((value / total) * 100).toFixed(1)}%`;
}
